import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useUserStore } from "@/store/userStore";
import { useParams, useSearchParams } from "react-router-dom";
import { CreateUserDto, RoleEnum } from "@/types";
import { filterParams } from "@/utils/filterParams";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateUserSchema } from "../schemas";
import { CreateUserFormData, CreateUserProps } from "../types";
import { DEFAULT_PAGE_SIZE, KEYS_SEARCH_PARAMS } from "@/utils";
import { useAuth } from "@/contexts/AuthContext";

const useCreateUser = ({
  storeId,
  companyId,
  companyName,
}: CreateUserProps) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);
  const currentPage = Number(params[KEYS_SEARCH_PARAMS.PAGE]) || 1;
  const currentSearchParam = String(params[KEYS_SEARCH_PARAMS.SEARCH] || "");
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { createUser, fetchUsersInStore,fetchMyUsersStore } = useUserStore();
  const { role } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
    clearErrors,
  } = useForm<CreateUserFormData>({
    defaultValues: {
      role: RoleEnum.USER,
      full_name: "",
      company_name: companyName,
      position: "",
      department: "",
      email: "",
      password: "",
    },
    resolver: zodResolver(CreateUserSchema),
  });

  const isUser = useMemo(
    () => watch("role") === RoleEnum.USER,
    [watch("role")],
  );

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const handleChangeRoleField = () => {
    setValue("full_name", "");
    setValue("company_name", companyName);
    setValue("department", "");
    setValue("email", "");
    setValue("position", "");
    setValue("password", "");
    clearErrors();
  };

  const onSubmit = async (data: CreateUserFormData) => {
    const isUser = data.role === RoleEnum.USER;

    const user: Partial<CreateUserDto> = {
      store_id: Number(storeId),
      email: data.email,
      password: data.password,
      role: data.role,
      full_name: data.full_name,
      company_id: companyId,
    };

    if (!isUser) {
      user.position = data.position;
      user.department = data.department;
    }

    setIsLoading(true);

    await createUser(filterParams(user))
      .then(() => {
        handleClose();
        const functionFetch = role === RoleEnum.STORE_MANAGER ? fetchMyUsersStore : fetchUsersInStore;
        functionFetch({
          id: String(id),
          page: currentPage,
          s: currentSearchParam,
          limit: DEFAULT_PAGE_SIZE,
        });
      })
      .finally(() => setIsLoading(false));
  };

  return {
    handleOpen,
    handleClose,
    open,
    handleSubmit,
    onSubmit,
    control,
    register,
    errors,
    isUser,
    handleChangeRoleField,
    isLoading,
  };
};

export { useCreateUser };
